import { useEffect, useState, useCallback, useRef } from "react";
import { useInfiniteProducts } from "@/hooks/useInfiniteProducts";
import { MobileProductCard } from "./MobileProductCard";
import { useIsMobile } from "@/hooks/use-mobile";

interface Product {
  id: string;
  title: string;
  price_cents: number;
  currency: string;
  main_image_url?: string;
  discount_percentage?: number;
  discounted_price_cents?: number;
  discount_start_date?: string;
  discount_end_date?: string;
}

export function InspirationFeed() {
  const isMobile = useIsMobile();
  const {
    products,
    isLoading,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage
  } = useInfiniteProducts();


  // Intersection Observer for infinite scroll
  const observerRef = useRef<IntersectionObserver | null>(null);
  const lastProductRef = useCallback((node: HTMLDivElement | null) => {
    if (isLoading || isFetchingNextPage) return;

    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage();
        }
      },
      {
        rootMargin: '100px', // Start loading 100px before reaching the bottom
      }
    );

    if (node) observerRef.current.observe(node);
  }, [isLoading, isFetchingNextPage, hasNextPage, fetchNextPage]);

  if (isError) {
    return (
      <div className="px-4 py-8">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Failed to load products</p>
          <button 
            onClick={() => window.location.reload()}
            className="text-primary hover:underline"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 pb-20 min-h-screen"> {/* Bottom padding for mobile navigation, min-height prevents layout shift */}
      {/* Initial Loading */}
      {isLoading && products.length === 0 ? (
        <div className="grid grid-cols-2 gap-4 pt-4">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="space-y-2">
              <div className="aspect-[4/5] bg-muted rounded-lg animate-pulse" />
              <div className="h-4 bg-muted rounded animate-pulse" style={{ width: '90%' }} />
              <div className="h-4 bg-muted rounded animate-pulse" style={{ width: '70%' }} />
              <div className="h-4 bg-muted rounded w-20 animate-pulse" />
            </div>
          ))}
        </div>
      ) : (
        <>
          {/* Product Grid */}
          <div className="grid grid-cols-2 gap-4 pt-4">
            {products.map((product: Product, index: number) => {
              // Add ref to last item for infinite scroll
              const isLastItem = index === products.length - 1;
              // Eagerly load first 4 images (2 rows on mobile) for better LCP
              const shouldLoadEager = index < 4;
              
              return (
                <div 
                  key={product.id}
                  ref={isLastItem ? lastProductRef : null}
                >
                  <MobileProductCard product={product} loadEager={shouldLoadEager} />
                </div>
              );
            })}
          </div>

          {/* Loading More Indicator */}
          {isFetchingNextPage && (
            <div className="mt-8">
              <div className="grid grid-cols-2 gap-4">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="space-y-2">
                    <div className="aspect-[4/5] bg-muted rounded-lg animate-pulse" />
                    <div className="h-4 bg-muted rounded animate-pulse" style={{ width: '90%' }} />
                    <div className="h-4 bg-muted rounded animate-pulse" style={{ width: '70%' }} />
                    <div className="h-4 bg-muted rounded w-20 animate-pulse" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* End of Feed */}
          {!hasNextPage && products.length > 0 && (
            <div className="text-center py-8">
              <p className="text-muted-foreground text-sm">
                You've reached the end! 🎉
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}